import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import CachedImg from "../../../components/CachedImg";
import { guardarColores, guardarFuentes } from "../datos/marcaApi";
import type { ColorMarca, MarcaDS, ResultadoEscritura } from "../datos/marcaApi";
import { useEscrituraVigente } from "../datos/useEscrituraVigente";
import { EditorColoresMarca } from "./EditorColoresMarca";
import { EditorTipografiasMarca } from "./EditorTipografiasMarca";

/**
 * Tab Visual de la pantalla Marca: logo, paleta de colores y tipografías —
 * la parte "real" del design system.
 *
 * Escribe `brand_profiles.colors` y `brand_profiles.fonts`, los MISMOS campos
 * que "Mi marca" → "Mi diseño" (`config_sections/CompanyData.tsx`), vía
 * `datos/marcaApi.ts`, que es donde vive la equivalencia de shape y
 * normalización entre las dos pantallas. El logo queda en solo lectura: se
 * sube en Configuración, porque además espeja a `company_profiles`.
 *
 * El guardado es EXPLÍCITO y de toda la tab a la vez (un botón "Guardar
 * cambios", no autosave). Que sea uno solo para colores y tipografías no es
 * estético: al guardar bien, la pantalla se recarga desde la base y el panel
 * se remonta, así que dos botones independientes se pisarían — guardar uno
 * descartaría en silencio lo editado del otro.
 */

interface MarcaVisualPanelProps {
  marca: MarcaDS;
  /**
   * Algo se escribió de verdad en `brand_profiles`. Se avisa aunque después
   * falle otra escritura de la misma tanda, y aunque el panel ya no esté
   * vigente: la base cambió, así que el resto de la app tiene que releer.
   */
  onMarcaEscrita: () => void;
  /** Guardado OK: el mensaje lo muestra `MarcaPage`. */
  onGuardado: (texto: string) => void;
  onError: (texto: string) => void;
}

const ETIQUETAS_ROL: Record<string, string> = {
  primary: "Principal",
  secondary: "Secundario",
  accent: "Acento",
  neutral: "Neutro",
};

const claseEtiquetaCampo = "text-[11px] font-medium uppercase tracking-wide text-[rgb(var(--brand-black-rgb)_/_0.5)]";
const claseCard = "rounded-2xl border border-[rgb(var(--brand-black-rgb)_/_0.08)] bg-white p-4";
const claseBotonSecundario =
  "rounded-full border border-[rgb(var(--brand-black-rgb)_/_0.12)] py-2 px-4 text-[12px] font-medium text-brand-black transition-colors hover:border-[rgb(var(--brand-black-rgb)_/_0.25)] disabled:opacity-50";
const claseBotonPrimario =
  "inline-flex items-center gap-1.5 rounded-full bg-brand-primary py-2 px-4 text-[12px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50";

/** `[principal, secundaria]`, con "" en el rol que la marca todavía no tiene. */
const familiasDeLaMarca = (marca: MarcaDS): string[] => [
  (marca.fonts?.suggested?.[0] ?? "").trim(),
  (marca.fonts?.suggested?.[1] ?? "").trim(),
];

const sinVacias = (familias: string[]) => familias.map((f) => f.trim()).filter(Boolean);

export const MarcaVisualPanel = ({
  marca,
  onMarcaEscrita,
  onGuardado,
  onError,
}: MarcaVisualPanelProps) => {
  const [editando, setEditando] = useState(false);
  const [colores, setColores] = useState<ColorMarca[]>([]);
  const [familias, setFamilias] = useState<string[]>(["", ""]);
  const [guardando, setGuardando] = useState(false);
  const sigueVigente = useEscrituraVigente(marca.id);

  const familiasGuardadas = familiasDeLaMarca(marca);
  const tipografiasGuardadas = sinVacias(familiasGuardadas);

  // Familias que se están previsualizando ahora mismo: mientras se edita, las
  // del borrador (para que el "Aa" cambie al elegir); si no, las guardadas.
  const clavePreview = sinVacias(editando ? familias : familiasGuardadas).join("|");

  // Carga las Google Fonts que se están previsualizando para que el "Aa" se
  // vea con la tipografía real y no con el fallback del navegador.
  //
  // El id del <link> y la reinyección dependen de LAS FAMILIAS, no de
  // `marca.id` — antes eran `…-${marca.id}` con un `if (getElementById) return`
  // y el <link> nunca se removía. Mientras la pantalla era de solo lectura eso
  // solo acumulaba <link> al cambiar de marca; desde que las tipografías se
  // editan acá, la marca es la misma y el guard hacía que la familia NUEVA no
  // se cargara nunca: el "Aa" seguía mostrando la vieja hasta recargar la
  // página. La limpieza remueve exactamente el elemento que creó este efecto,
  // así que tampoco se acumulan.
  //
  // El mismo patrón viejo sigue en `CompanyData.tsx:407-423` y NO se tocó: es
  // producción y allá el síntoma no es alcanzable igual (ese editor recarga la
  // marca entera desde la base al guardar).
  useEffect(() => {
    const familiasPreview = clavePreview.split("|").filter(Boolean);
    if (familiasPreview.length === 0) return;
    const consulta = familiasPreview
      .map((f) => f.replace(/\s+/g, "+"))
      .map((f) => `family=${f}:wght@400;500;600`)
      .join("&");
    const link = document.createElement("link");
    link.id = `estudio-marketing-marca-fonts-${clavePreview
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")}`;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?${consulta}&display=swap`;
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, [clavePreview]);

  const entrarAEditar = () => {
    // El `typeof === "object"` no es paranoia de tipos: hay marcas reales cuyo
    // `colors` guarda strings sueltos en vez de objetos. Copiarlos con spread
    // los convertiría en `{0:"#",1:"f",…}` y el borrador saldría distinto del
    // original sin que el usuario haya tocado nada — o sea, `cambiaronColores`
    // daría `true` de arranque y una edición de tipografías arrastraría una
    // escritura de paleta que nadie pidió.
    setColores(marca.colors.map((c) => (c && typeof c === "object" ? { ...c } : c)));
    setFamilias(familiasGuardadas);
    setEditando(true);
  };

  const cambiaronColores = JSON.stringify(colores) !== JSON.stringify(marca.colors);
  const cambiaronFamilias =
    JSON.stringify(sinVacias(familias)) !== JSON.stringify(tipografiasGuardadas);
  const hayCambios = cambiaronColores || cambiaronFamilias;

  /**
   * Guarda lo que el usuario cambió, y SOLO lo que cambió.
   *
   * `colors` y `fonts` son dos columnas independientes y se escriben por
   * separado: que una falle no puede bloquear a la otra. Eso importa por un
   * caso real y no hipotético — hay marcas cuya paleta viene del onboarding con
   * hex sin `#` (`FFFFFF`), con `hex: null` o con strings sueltos en vez de
   * objetos. Si la validación de la paleta cortara la tanda entera, el dueño de
   * una de esas marcas no podría guardar ni una tipografía sin antes arreglar a
   * mano colores que él nunca puso — y en "Mi diseño" esa misma marca guarda
   * sin problema. La validación de hex sigue existiendo (en `guardarColores`),
   * pero solo puede frenar a `colors`, y solo si el usuario tocó la paleta:
   * `cambiaronColores` es la condición para siquiera intentar esa escritura.
   *
   * No hay transacción entre las dos, así que se asume el resultado parcial y
   * se lo comunica: si UNA impactó, se avisa igual a la app (`onMarcaEscrita`)
   * para que todos relean, y el cartel dice qué se guardó y qué no. Callarse la
   * mitad guardada sería justo lo que esta pantalla existe para evitar: la base
   * y "Mi diseño" con el dato nuevo, y esta pantalla mostrando el viejo.
   */
  const guardar = async () => {
    // Doble freno al doble click: el botón queda `disabled` mientras guarda y,
    // por si un click llega en el mismo tick, el estado se chequea acá.
    if (guardando || !hayCambios) return;
    // El id se captura ANTES del await: si el usuario cambia de marca mientras
    // la escritura está en vuelo, `sigueVigente` corta y la respuesta no se
    // aplica sobre la marca nueva (la escritura ya impactó igual, sobre la
    // marca vieja, que es lo que el usuario pidió).
    const idAlDisparar = marca.id;
    setGuardando(true);

    // `null` = no se intentó (no había nada que cambiar en esa columna).
    let resColores: ResultadoEscritura | null = null;
    let resFuentes: ResultadoEscritura | null = null;

    if (cambiaronColores) {
      resColores = await guardarColores(idAlDisparar, colores);
    }
    if (cambiaronFamilias) {
      // Se manda el objeto `fonts` entero para no perder `style` ni cualquier
      // otra clave que la fila ya tuviera — ver `guardarFuentes`.
      resFuentes = await guardarFuentes(idAlDisparar, {
        ...(marca.fonts ?? {}),
        suggested: sinVacias(familias),
      });
    }

    // Antes del chequeo de vigencia a propósito: si algo se escribió, la base
    // cambió, y eso hay que avisarlo aunque el usuario ya se haya ido o haya
    // cambiado de marca. Es un evento de ventana, no un `setState`.
    if (resColores?.ok || resFuentes?.ok) onMarcaEscrita();

    if (!sigueVigente(idAlDisparar)) return;
    setGuardando(false);

    const fallos: string[] = [];
    if (resColores && !resColores.ok) {
      fallos.push(resColores.error ?? "No se pudieron guardar los colores.");
    }
    if (resFuentes && !resFuentes.ok) {
      fallos.push(resFuentes.error ?? "No se pudieron guardar las tipografías.");
    }

    if (fallos.length === 0) {
      setEditando(false);
      onGuardado("Listo: guardamos los cambios en tu marca.");
      return;
    }

    const guardado: string[] = [];
    if (resColores?.ok) guardado.push("los colores");
    if (resFuentes?.ok) guardado.push("las tipografías");
    onError(
      guardado.length > 0
        ? `Guardamos ${guardado.join(" y ")}. Lo demás no: ${fallos.join(" ")}`
        : fallos.join(" "),
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[12px] text-[rgb(var(--brand-black-rgb)_/_0.5)]">
          Colores y tipografías de tu marca. Se comparten con Mi marca → Mi diseño.
        </p>
        {editando ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setEditando(false)}
              disabled={guardando}
              className={claseBotonSecundario}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={guardar}
              disabled={guardando || !hayCambios}
              className={claseBotonPrimario}
            >
              {guardando && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {guardando ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        ) : (
          <button type="button" onClick={entrarAEditar} className={claseBotonSecundario}>
            Editar
          </button>
        )}
      </div>

      <div className={claseCard}>
        <p className={claseEtiquetaCampo}>Logo</p>
        {marca.logo_url ? (
          <div className="mt-2 h-20 w-20 overflow-hidden rounded-xl border border-[rgb(var(--brand-black-rgb)_/_0.08)] bg-white p-2">
            <CachedImg
              src={marca.logo_url}
              alt="Logo de la marca"
              className="h-full w-full object-contain"
            />
          </div>
        ) : (
          <p className="mt-2 text-[13px] text-[rgb(var(--brand-black-rgb)_/_0.5)]">
            Todavía no subiste un logo para esta marca.
          </p>
        )}
        {editando && (
          <p className="mt-2 text-[11px] text-[rgb(var(--brand-black-rgb)_/_0.4)]">
            El logo se cambia en Configuración → Mis marcas.
          </p>
        )}
      </div>

      <div className={claseCard}>
        <p className={claseEtiquetaCampo}>Paleta de colores</p>
        {editando ? (
          <EditorColoresMarca
            colores={colores}
            onCambiar={setColores}
            deshabilitado={guardando}
          />
        ) : marca.colors.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-4">
            {marca.colors.map((c, idx) => (
              <div key={`${c.hex}-${idx}`} className="flex flex-col items-center gap-1.5">
                {/* Hex dinámico de la marca del usuario: SIEMPRE por estilo
                    inline, nunca por clase de Tailwind — no hay forma de que
                    el compilador conozca de antemano una clase para un color
                    que viene de la base. */}
                <div
                  className="h-12 w-12 rounded-full border border-[rgb(var(--brand-black-rgb)_/_0.1)] shadow-sm"
                  style={{ backgroundColor: c.hex }}
                  title={c.name ? `${c.name} · ${c.hex}` : c.hex}
                />
                <p className="font-mono text-[10px] text-[rgb(var(--brand-black-rgb)_/_0.55)]">{c.hex}</p>
                {c.role && (
                  <p className="text-[10px] text-[rgb(var(--brand-black-rgb)_/_0.4)]">
                    {ETIQUETAS_ROL[c.role] ?? c.role}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-[13px] text-[rgb(var(--brand-black-rgb)_/_0.5)]">
            Todavía no cargaste colores para esta marca.
          </p>
        )}
      </div>

      <div className={claseCard}>
        <p className={claseEtiquetaCampo}>Tipografías</p>
        {editando ? (
          <EditorTipografiasMarca
            familias={familias}
            familiasOriginales={tipografiasGuardadas}
            onCambiar={setFamilias}
            deshabilitado={guardando}
          />
        ) : tipografiasGuardadas.length > 0 ? (
          <>
            <div className="mt-3 grid max-w-md grid-cols-2 gap-4">
              {tipografiasGuardadas.map((font, idx) => (
                <div
                  key={`${font}-${idx}`}
                  className="rounded-xl border border-[rgb(var(--brand-black-rgb)_/_0.08)] p-4 text-center"
                >
                  <p
                    className="text-[32px] leading-none text-brand-black"
                    style={{ fontFamily: `"${font}", sans-serif` }}
                  >
                    Aa
                  </p>
                  <p className="mt-2 truncate text-[12px] text-brand-black">{font}</p>
                  <p className="mt-0.5 text-[10px] uppercase tracking-wide text-[rgb(var(--brand-black-rgb)_/_0.4)]">
                    {idx === 0 ? "Principal" : "Secundaria"}
                  </p>
                </div>
              ))}
            </div>
            {marca.fonts?.style && (
              <p className="mt-3 text-[12px] italic text-[rgb(var(--brand-black-rgb)_/_0.55)]">{marca.fonts.style}</p>
            )}
          </>
        ) : (
          <p className="mt-2 text-[13px] text-[rgb(var(--brand-black-rgb)_/_0.5)]">
            Todavía no cargaste tipografías para esta marca.
          </p>
        )}
      </div>
    </div>
  );
};

export default MarcaVisualPanel;
